import React, { useState, useEffect, useRef } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Upload, 
  ColorPicker, 
  Card, 
  Tabs,
  Switch,
  message,
  Spin,
  Alert,
  Divider,
  Progress,
  Tooltip,
  Select, 
  Collapse,
} from 'antd';
import { 
  UploadOutlined, 
  SaveOutlined, 
  ReloadOutlined,
  PictureOutlined,
  BgColorsOutlined,
  MailOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Option, OptGroup } = Select;

const WhitelabelingTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [isAgencyLicense, setIsAgencyLicense] = useState(false);
  const [checkingLicense, setCheckingLicense] = useState(true);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [emailTemplatePreview, setEmailTemplatePreview] = useState('');
  const [maxDomains, setMaxDomains] = useState(0);
  const [usedDomains, setUsedDomains] = useState(0);
  const [expiryDate, setExpiryDate] = useState('');
  const [previewColors, setPreviewColors] = useState({
    primaryColor: '#1890ff',
    menuBgColor: '#001529',
    menuTextColor: '#ffffff'
  });
  const [headerTextPreview, setHeaderTextPreview] = useState('AI Workflow Automation');
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  
  // Sample data for email template preview
  const [previewData, setPreviewData] = useState({
    site_name: "Example Site",
    task_id: "123",
    workflow_name: "Sample Workflow",
    input_type: "approval",
    content: "This is sample content that needs approval.",
    instructions: "Please review and approve this content.",
    admin_url: "#",
    site_icon_url: "" // Will be populated with logo
  });
  
  useEffect(() => {
    checkLicenseStatus();
    fetchWhitelabelSettings();
  }, []);

  useEffect(() => {
    setPreviewData(prev => ({
      ...prev,
      site_icon_url: logoPreview || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
    }));
  }, [logoPreview]);

  const checkLicenseStatus = async () => {
    setCheckingLicense(true);
    // Safety timeout
    const timeoutId = setTimeout(() => {
      setCheckingLicense(false);
      message.error('License check timed out. Please try again.');
    }, 15000);
    
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/check-agency-license`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      setIsLicenseValid(response.data.is_active);
      setIsAgencyLicense(response.data.is_agency);
      setMaxDomains(response.data.max_domains);
      setUsedDomains(response.data.used_domains);
      setExpiryDate(response.data.expiry_date);
      
    } catch (error) {
      console.error('Error checking license status:', error);
      setIsLicenseValid(false);
      setIsAgencyLicense(false);
      message.error('Failed to check license status');
    } finally {
      clearTimeout(timeoutId);
      setCheckingLicense(false);
    }
  };

  const fetchWhitelabelSettings = async () => {
    setLoading(true);
    // Safety timeout
    const timeoutId = setTimeout(() => {
      setLoading(false);
      message.error('Loading settings timed out. Please try again.');
    }, 15000);
    
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/whitelabel-settings`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      const settings = response.data;
     
      // Set logo preview if exists
      if (settings.logo_url) {
        setLogoPreview(settings.logo_url);
        
        // Also update the site_icon_url in previewData
        setPreviewData(prev => ({
          ...prev,
          site_icon_url: settings.logo_url
        }));
      }
      
      // Update preview states
      setHeaderTextPreview(settings.header_text || 'AI Workflow Automation');
      setPreviewColors({
        primaryColor: settings.primary_color || '#1890ff',
        menuBgColor: settings.menu_bg_color || '#001529',
        menuTextColor: settings.menu_text_color || '#ffffff'
      });
      
      // Set email template preview
      setEmailTemplatePreview(settings.custom_email_template || '');
      
      // Set form values
      form.setFieldsValue({
        enable_whitelabel: settings.enable_whitelabel || false,
        logo_url: settings.logo_url || '',
        header_text: settings.header_text || 'AI Workflow Automation',
        primary_color: settings.primary_color || '#1890ff',
        menu_bg_color: settings.menu_bg_color || '#001529',
        menu_text_color: settings.menu_text_color || '#ffffff',
        custom_email_subject: settings.custom_email_subject || 'New Task Awaiting Your Input',
        custom_email_template: settings.custom_email_template || ''
      });
      
    } catch (error) {
      console.error('Error fetching whitelabel settings:', error);
      message.error('Failed to fetch whitelabel settings');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setSaveLoading(true);
    
    try {
      // Get the current form values
      const formValues = form.getFieldsValue(true); // Force get ALL values
      
      // Ensure we have all required fields with defaults if missing
      const completeValues = {
        // Basic settings with defaults
        enable_whitelabel: formValues.enable_whitelabel || false,
        logo_url: formValues.logo_url || '',
        header_text: formValues.header_text || 'AI Workflow Automation',
        
        // Colors with defaults - ensure we handle color objects
        primary_color: typeof formValues.primary_color === 'object' 
          ? formValues.primary_color.toHexString() 
          : (formValues.primary_color || '#1890ff'),
        menu_bg_color: typeof formValues.menu_bg_color === 'object' 
          ? formValues.menu_bg_color.toHexString() 
          : (formValues.menu_bg_color || '#001529'),
        menu_text_color: typeof formValues.menu_text_color === 'object' 
          ? formValues.menu_text_color.toHexString() 
          : (formValues.menu_text_color || '#ffffff'),
        
        // Email settings with defaults
        custom_email_subject: formValues.custom_email_subject || 'New Task Awaiting Your Input',
        custom_email_template: formValues.custom_email_template || ''
      };
      
      // Now save the complete values
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/whitelabel-settings`,
        completeValues,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      message.success('Whitelabel settings saved successfully');
      
      // Update the UI to reflect the saved values
      setLogoPreview(completeValues.logo_url);
      setHeaderTextPreview(completeValues.header_text);
      setPreviewColors({
        primaryColor: completeValues.primary_color,
        menuBgColor: completeValues.menu_bg_color,
        menuTextColor: completeValues.menu_text_color
      });
      
      // Apply changes immediately
      if (window.wpAiWorkflowsApplySettings) {
        window.wpAiWorkflowsApplySettings({
          enabled: completeValues.enable_whitelabel ? '1' : '0',
          logoUrl: completeValues.logo_url,
          primaryColor: completeValues.primary_color,
          menuBgColor: completeValues.menu_bg_color,
          menuTextColor: completeValues.menu_text_color
        });
      }
      
    } catch (error) {
      console.error('Error saving whitelabel settings:', error);
      message.error('Failed to save whitelabel settings');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogoUpload = async (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    
    if (info.file.status === 'done') {
      if (info.file.response && info.file.response.success) {
        const logoUrl = info.file.response.data.url;
        setLogoPreview(logoUrl);
        
        // Get current form values and only update the logo_url
        const currentValues = form.getFieldsValue();
        form.setFieldsValue({ 
          ...currentValues,
          logo_url: logoUrl 
        });
        
        message.success('Logo uploaded successfully');
      } else {
        message.error(info.file.response?.message || 'Logo upload failed');
      }
    } else if (info.file.status === 'error') {
      message.error(`Logo upload failed: ${info.file.error?.message || 'Unknown error'}`);
    }
  };

  const getDefaultEmailTemplate = () => {
    return `<!DOCTYPE html>
  <html>
  <head>
      <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  </head>
  <body style="margin: 0; padding: 0; background-color: #F7F7F7; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
      <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #F7F7F7;">
          <tr>
              <td align="center" style="padding: 20px 0;">
                  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="600" style="background-color: #ffffff; border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
                      <!-- Header -->
                      <tr>
                          <td align="center" style="padding: 30px 30px 20px 30px;">
                              <img src="{{site_icon_url}}" alt="{{site_name}}" width="auto" height="60" style="display: block; max-width: 240px; max-height: 60px; object-fit: contain; margin-bottom: 15px;">
                              <h1 style="color: #1D1D1F; font-size: 24px; font-weight: 600; margin: 0 0 10px 0;">New Task Awaiting Your Input</h1>
                              <p style="color: #86868B; font-size: 16px; margin: 0;">Action required in {{site_name}}</p>
                          </td>
                      </tr>
  
                      <!-- Content -->
                      <tr>
                          <td style="padding: 0 30px;">
                              <p style="color: #1D1D1F; font-size: 16px; line-height: 1.6;">You have a new task waiting for your input in the AI Workflow Automation plugin.</p>
                              
                              <!-- Task Details Box -->
                              <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #F5F5F7; border-radius: 12px; margin: 20px 0;">
                                  <tr>
                                      <td style="padding: 20px;">
                                          <!-- Workflow -->
                                          <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 15px;">
                                              <tr>
                                                  <td>
                                                      <p style="color: #86868B; font-size: 14px; margin: 0 0 4px 0;">Workflow</p>
                                                      <p style="color: #1D1D1F; font-size: 16px; margin: 0;">{{workflow_name}}</p>
                                                  </td>
                                              </tr>
                                          </table>
  
                                          <!-- Type -->
                                          <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 15px;">
                                              <tr>
                                                  <td>
                                                      <p style="color: #86868B; font-size: 14px; margin: 0 0 4px 0;">Type</p>
                                                      <p style="color: #1D1D1F; font-size: 16px; margin: 0;">{{input_type}}</p>
                                                  </td>
                                              </tr>
                                          </table>
  
                                          <!-- Content -->
                                          <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 15px;">
                                              <tr>
                                                  <td>
                                                      <p style="color: #86868B; font-size: 14px; margin: 0 0 4px 0;">Content</p>
                                                      <p style="color: #1D1D1F; font-size: 16px; margin: 0; white-space: pre-wrap;">{{content}}</p>
                                                  </td>
                                              </tr>
                                          </table>
  
                                          <!-- Instructions -->
                                          <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
                                              <tr>
                                                  <td>
                                                      <p style="color: #86868B; font-size: 14px; margin: 0 0 4px 0;">Instructions</p>
                                                      <p style="color: #1D1D1F; font-size: 16px; margin: 0; white-space: pre-wrap;">{{instructions}}</p>
                                                  </td>
                                              </tr>
                                          </table>
                                      </td>
                                  </tr>
                              </table>
  
                              <!-- Action Button -->
                              <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin: 20px 0;">
                                  <tr>
                                      <td align="center">
                                          <a href="{{admin_url}}" style="background-color: #0071E3; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; font-weight: 500; display: inline-block;">View and Action Task</a>
                                      </td>
                                  </tr>
                              </table>
                          </td>
                      </tr>
  
                      <!-- Footer -->
                      <tr>
                          <td align="center" style="padding: 30px;">
                              <p style="color: #86868B; font-size: 13px; margin: 0;">Powered by AI Workflow Automation</p>
                          </td>
                      </tr>
                  </table>
              </td>
          </tr>
      </table>
  </body>
  </html>`;
  };
  
  const resetToDefaults = () => {
    // Default values
    const defaults = {
      enable_whitelabel: false,
      logo_url: '',
      header_text: 'AI Workflow Automation',
      font_family: 'IBM Plex Mono, monospace', // Add font_family default
      primary_color: '#1890ff',
      menu_bg_color: '#001529',
      menu_text_color: '#ffffff',
      custom_email_subject: 'New Task Awaiting Your Input',
      custom_email_template: '',
    };
    
    // Reset form
    form.setFieldsValue(defaults);
    
    // Reset previews
    setLogoPreview(null);
    setHeaderTextPreview(defaults.header_text);
    setEmailTemplatePreview(defaults.custom_email_template);
    setPreviewColors({
      primaryColor: defaults.primary_color,
      menuBgColor: defaults.menu_bg_color,
      menuTextColor: defaults.menu_text_color
    });
    
    message.info('Reset to default settings');
  };

  const renderLicenseInfo = () => {
    if (checkingLicense) {
      return <Spin tip="Checking license status..." />;
    }
    
    if (!isLicenseValid) {
      return (
        <Alert
          type="error"
          message="License Required"
          description="You need a valid license to use the whitelabeling feature."
          showIcon
        />
      );
    }
    
    if (!isAgencyLicense) {
      return (
        <Alert
          type="warning"
          message="Agency License Required"
          description="Whitelabeling is only available with an Agency License. Please upgrade your license to access this feature."
          showIcon
        />
      );
    }
    
    return (
      <div style={{ marginBottom: 20 }}>
        <Alert
          type="success"
          message="Agency License Active"
          description={
            <div>
              <Space direction="vertical">
                <Text>Your agency license allows you to customize the plugin branding.</Text>
                <Space>
                  <Text strong>Domains:</Text>
                  <Text>{usedDomains} used of {maxDomains} allowed</Text>
                  <Progress 
                    percent={Math.round((usedDomains / maxDomains) * 100)} 
                    size="small" 
                    style={{ width: 100 }} 
                  />
                </Space>
                {expiryDate && (
                  <Space>
                    <Text strong>Expires:</Text>
                    <Text>{new Date(expiryDate).toLocaleDateString()}</Text>
                  </Space>
                )}
              </Space>
            </div>
          }
          showIcon
        />
      </div>
    );
  };

  
  // Handle color changes for previews
  const handleColorChange = (colorKey, value) => {
    // Update form value
    const formUpdate = {};
    formUpdate[colorKey] = value;
    form.setFieldsValue(formUpdate);
    
    // Update preview state
    setPreviewColors(prev => ({
      ...prev,
      [colorKey]: typeof value === 'object' ? value.toHexString() : value
    }));
  };

  if (checkingLicense) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <Spin size="large" tip="Checking license status..." />
      </div>
    );
  }

  // If not an agency license, show upgrade message
  if (!isAgencyLicense || !isLicenseValid) {
    return (
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          {renderLicenseInfo()}
          <Title level={4}>Whitelabeling</Title>
          <Paragraph>
            Whitelabeling allows you to customize the appearance of the plugin with your own branding. 
            This feature is exclusively available with an Agency License.
          </Paragraph>
          <Divider />
          <Space>
            <Button type="primary" href="https://wpaiworkflowautomation.com/pricing" target="_blank">
              Upgrade to Agency License
            </Button>
            <Button onClick={checkLicenseStatus} loading={checkingLicense}>
              Refresh License Status
            </Button>
          </Space>
        </Space>
      </Card>
    );
  }

  const handleExportSettings = () => {
    // Get current form values
    const formValues = form.getFieldsValue();
    
    // Make sure we include all necessary fields
    const exportData = {
      // Basic settings
      enable_whitelabel: formValues.enable_whitelabel || false,
      logo_url: formValues.logo_url || '',
      header_text: formValues.header_text || 'AI Workflow Automation',
      
      // Colors - ensure we export hex strings
      primary_color: typeof formValues.primary_color === 'object' 
        ? formValues.primary_color.toHexString() 
        : (formValues.primary_color || '#1890ff'),
      menu_bg_color: typeof formValues.menu_bg_color === 'object' 
        ? formValues.menu_bg_color.toHexString() 
        : (formValues.menu_bg_color || '#001529'),
      menu_text_color: typeof formValues.menu_text_color === 'object' 
        ? formValues.menu_text_color.toHexString() 
        : (formValues.menu_text_color || '#ffffff'),
      
      // Email settings
      custom_email_subject: formValues.custom_email_subject || 'New Task Awaiting Your Input',
      custom_email_template: formValues.custom_email_template || ''
    };
    
    // Create a JSON file and trigger download
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'whitelabel-settings.json';
    
    // Create a link element to trigger download
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    message.success('Settings exported successfully');
  };
  
  const handleImportClick = () => {
    // Trigger hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportLoading(true);
    
    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('settings_file', file);
      
      // Call the API endpoint
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/import-whitelabel-settings`,
        formData,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        message.success('Settings imported successfully');
        
        // Update UI with imported settings
        if (response.data.settings) {
          // Update form values
          form.setFieldsValue(response.data.settings);
          
          // Update preview states
          if (response.data.settings.logo_url) {
            setLogoPreview(response.data.settings.logo_url);
            
            setPreviewData(prev => ({
              ...prev,
              site_icon_url: response.data.settings.logo_url
            }));
          }
          
          setHeaderTextPreview(response.data.settings.header_text || 'AI Workflow Automation');
          setPreviewColors({
            primaryColor: response.data.settings.primary_color || '#1890ff',
            menuBgColor: response.data.settings.menu_bg_color || '#001529',
            menuTextColor: response.data.settings.menu_text_color || '#ffffff'
          });
          
          if (response.data.settings.custom_email_template) {
            setEmailTemplatePreview(response.data.settings.custom_email_template);
          }
        }
      } else {
        message.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing settings:', error);
      message.error(error.response?.data?.message || 'Import failed');
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Form
    form={form}
    layout="vertical"
    onFinish={onFinish}
    initialValues={{
        enable_whitelabel: false,
        logo_url: '',
        header_text: 'AI Workflow Automation',
        font_family: 'IBM Plex Mono, monospace', // Add font_family default
        primary_color: '#1890ff',
        menu_bg_color: '#001529',
        menu_text_color: '#ffffff',
        custom_email_subject: 'New Task Awaiting Your Input',
        custom_email_template: ''
    }}
    >
      <Card 
        title="Whitelabeling Settings" 
        extra={
          <Space>
              <Tooltip title="Import settings from file">
              <Button 
                icon={<ImportOutlined />} 
                onClick={handleImportClick}
                loading={importLoading}
                disabled={saveLoading}
              >
                Import
              </Button>
            </Tooltip>
            
            {/* Add export button */}
            <Tooltip title="Export settings to file">
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleExportSettings}
                disabled={saveLoading || importLoading}
              >
                Export
              </Button>
            </Tooltip>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetToDefaults}
              disabled={saveLoading}
            >
              Reset to Defaults
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              htmlType="submit" 
              loading={saveLoading}
            >
              Save Settings
            </Button>
          </Space>
        }
      >
        {renderLicenseInfo()}
        
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Spin tip="Loading settings..." />
          </div>
        ) : (
          <>
            <Form.Item 
              name="enable_whitelabel" 
              valuePropName="checked"
              label="Enable Whitelabeling"
            >
              <Switch />
            </Form.Item>
            
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane 
                tab={<span><PictureOutlined /> Logo</span>} 
                key="1"
                >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                    message="Logo Replacement"
                    description="Upload your own logo to replace the default 'AI Workflow Automation' branding in the plugin header."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    />
                    
                    <Form.Item 
                    label="Logo URL"
                    name="logo_url"
                    extra="The URL of your custom logo image"
                    >
                    <Input />
                    </Form.Item>
                    
                    <Upload
                    name="logo"
                    action={`${config.apiSettings.root}wp-ai-workflows/v1/upload-logo?_wpnonce=${config.apiSettings.nonce}`}
                    headers={{ 'X-WP-Nonce': config.apiSettings.nonce }}
                    onChange={handleLogoUpload}
                    showUploadList={false}
                    accept=".jpg,.jpeg,.png,.gif,.svg"
                    >
                    <Button icon={<UploadOutlined />}>Upload Logo</Button>
                    </Upload>
                    
                    <div style={{ marginTop: 16 }}>
                    <Text type="secondary">
                        Recommended logo specifications:
                    </Text>
                    <ul style={{ color: '#8c8c8c', marginTop: 8 }}>
                        <li>File formats: PNG, JPG, SVG (recommended)</li>
                        <li>Size: Approximately 180-520px wide</li>
                        <li>Height: 32px (logos will be scaled to this height)</li>
                        <li>For best results, use an image with transparency</li>
                        <li>Light-colored logos work best with dark menu backgrounds</li>
                    </ul>
                    </div>
                    
                    {logoPreview && (
                    <div style={{ marginTop: 16 }}>
                        <Text strong>Logo Preview:</Text>
                        <div style={{ 
                        marginTop: 8, 
                        padding: 16, 
                        background: form.getFieldValue('menu_bg_color') || '#001529', 
                        borderRadius: 4,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center'
                        }}>
                        <img 
                            src={logoPreview} 
                            alt="Logo Preview" 
                            style={{ maxHeight: 32, maxWidth: '100%' }}
                            onError={(e) => {
                            message.error('Failed to load logo preview');
                            setLogoPreview(null);
                            }}
                        />
                        </div>
                    </div>
                    )}
                </Space>
                </TabPane>
              
              <TabPane 
                tab={<span><BgColorsOutlined /> Colors</span>} 
                key="2"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item 
                    label="Primary Color"
                    name="primary_color"
                    extra="Main color used for buttons, links, and active elements"
                  >
                    <ColorPicker 
                      format="hex" 
                      allowClear={false}
                      onChange={(color) => handleColorChange('primary_color', color)}
                    />
                  </Form.Item>
                  
                  <Form.Item 
                    label="Menu Background Color"
                    name="menu_bg_color"
                    extra="Background color for the main navigation menu"
                  >
                    <ColorPicker 
                      format="hex" 
                      allowClear={false}
                      onChange={(color) => handleColorChange('menu_bg_color', color)}
                    />
                  </Form.Item>
                  
                  <Form.Item 
                    label="Menu Text Color"
                    name="menu_text_color"
                    extra="Color of the text in the navigation menu"
                  >
                    <ColorPicker 
                      format="hex" 
                      allowClear={false} 
                      onChange={(color) => handleColorChange('menu_text_color', color)}
                    />
                  </Form.Item>
                  
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Menu Preview:</Text>
                    <div 
                      style={{ 
                        marginTop: 8, 
                        padding: '8px 16px',
                        background: previewColors.menuBgColor,
                        color: previewColors.menuTextColor,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 32
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>Menu Item 1</span>
                      <span style={{ color: previewColors.primaryColor, fontWeight: 'bold' }}>
                        Selected Item
                      </span>
                      <span>Menu Item 3</span>
                    </div>
                  </div>
                </Space>
              </TabPane>
              
              <TabPane 
                tab={<span><MailOutlined /> Email Template</span>} 
                key="3"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {/* Add explanation note */}
                  <Alert
                    message="Human Task Email Notifications"
                    description="This template is used for email notifications sent to users when a human input task is assigned to them. Users receive these emails when approval or modification is required in a workflow."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <Form.Item 
                    label="Email Subject"
                    name="custom_email_subject"
                    extra="Subject line for task notification emails"
                  >
                    <Input />
                  </Form.Item>
                  
                  <Form.Item 
                    label="Custom Email Template"
                    name="custom_email_template"
                    extra={
                      <span>
                        HTML template for task notification emails. Use placeholders listed below to customize your template.
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={() => {
                            const defaultTemplate = getDefaultEmailTemplate();
                            form.setFieldsValue({ custom_email_template: defaultTemplate });
                            setEmailTemplatePreview(defaultTemplate);
                          }}
                        >
                          Reset to Default Template
                        </Button>
                      </span>
                    }
                  >
                    <TextArea 
                      rows={12} 
                      onChange={(e) => setEmailTemplatePreview(e.target.value)} 
                    />
                  </Form.Item>

                  {/* Show default template reference */}
                  <Collapse ghost>
                    <Panel header="Default Template Reference" key="defaultTemplate">
                      <div style={{ 
                        maxHeight: '400px', 
                        overflow: 'auto', 
                        padding: '16px', 
                        background: '#f5f5f5', 
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        fontSize: '12px'
                      }}>
                        <Text type="secondary">View the default template as a reference:</Text>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                          {getDefaultEmailTemplate()}
                        </pre>
                      </div>
                    </Panel>
                  </Collapse>
                  
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Template Preview:</Text>
                    <div style={{ 
                      marginTop: 8, 
                      padding: 16, 
                      background: '#f5f5f5', 
                      borderRadius: 4,
                      maxHeight: 400,
                      overflow: 'auto',
                      border: '1px solid #e8e8e8'
                    }}>
                      {emailTemplatePreview ? (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: emailTemplatePreview.replace(/\{\{(\w+)\}\}/g, (match, placeholder) => {
                              // Special handling for site_icon_url
                              if (placeholder === 'site_icon_url') {
                                return logoPreview || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                              }
                              return previewData[placeholder] || match;
                            })
                          }} 
                        />
                      ) : (
                        <Text type="secondary">No custom template defined. System default will be used.</Text>
                      )}
                    </div>
                  </div>
                  
                  <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    message="Available Placeholders"
                    description={
                      <ul>
                        <li><code>{'{{site_name}}'}</code> - The name of your WordPress site</li>
                        <li><code>{'{{task_id}}'}</code> - The ID of the task</li>
                        <li><code>{'{{workflow_name}}'}</code> - The name of the workflow</li>
                        <li><code>{'{{input_type}}'}</code> - The type of input required (approval/modification)</li>
                        <li><code>{'{{content}}'}</code> - The content to be reviewed</li>
                        <li><code>{'{{instructions}}'}</code> - The instructions for the task</li>
                        <li><code>{'{{admin_url}}'}</code> - The URL to the tasks page</li>
                        <li><code>{'{{site_icon_url}}'}</code> - The URL to your custom logo</li>
                      </ul>
                    }
                  />
                </Space>
              </TabPane>
            </Tabs>
          </>
        )}
      </Card>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleFileUpload}
      />
    </Form>
  );
};

export default WhitelabelingTab;